<template>
  <div :class="['quill-editor-section', wrapperExtraClasses]">
    <quill-toolbar
        v-if="showToolbar"
        ref="toolbar"
        :canInsertImages="canInsertImages"
        :showImages="showImages">
    </quill-toolbar>
    <insert-images
        v-if="canInsertImages"
        :campaignId="campaignId"
        :dragIcon="dragIcon"
        :imageToResize="imageToResize"
        :showImages="showImages"
        @contentUpdate="contentUpdate"
        @insertImage="insertImage"
        @setResizeDimension="setResizeDimension">
    </insert-images>
    <div :class="['text-editor', editorExtraClasses]" ref="editor"></div>
  </div>
</template>

<script>
  import InsertImages from './InsertImages'
  import _Quill from 'quill'
  import QuillToolbar from './QuillToolbar'
  const Quill = window.Quill || _Quill

  export default {
    props: {
      campaignId: String,
      canInsertImages: Boolean,
      editorContent: String,
      editorExtraClasses: String,
      toolbarClass: String,
      showToolbar: Boolean,
      wrapperExtraClasses: String,
    },

    name: 'TextEditor',

    components: {
      InsertImages,
      QuillToolbar
    },

    data() {
      return {
        delayedUpdate: null,
        dragIcon: null,
        editor: null,
        editorIndex: null,
        imageToResize: null,
        showImages: false,
      }
    },

    methods: {
      configureInsertingImages() {
        this.editor.getModule('toolbar').addHandler('image', () => this.toggleOpenImages())
        this.createQuillResizeableImage()
        this.editor.root.addEventListener('click', (event) => this.listenForImageClicks(event))
        this.resetResize()
      },

      contentUpdate() {
        const editorContent = this.editor?.root?.innerHTML
        this.$emit('setEditorContent', editorContent)
      },

      createQuillResizeableImage() {
        const ImageBlot = Quill.import('formats/image')
        class resizeableImage extends ImageBlot {
          static create(value) {
            let node = super.create(value)
            if (typeof value === 'string') {
              node.removeAttribute('src')
              node.innerHTML = `<img src="${value}"><span class="drag-icon"></span>`
            }
            return node
          }
        }
        resizeableImage.blotName = 'resizeableImage'
        resizeableImage.className = 'image-resize'
        resizeableImage.tagName = 'span'
        Quill.register({'formats/resizeableImage': resizeableImage})
      },

      delayUpdateEditor() {
        clearTimeout(this.delayedUpdate)
        if (this.editor.root.innerHTML !== this.editorContent) {
          this.delayedUpdate = setTimeout(() => this.contentUpdate(), 500)
        }
      },

      initQuill() {
        this.initQuillOptions()
        const modules = {toolbar: this.$refs.toolbar.$el}
        let options = { modules, theme: 'snow' },
          content = this.editorContent ? this.editorContent : ''
        this.editor = new Quill(this.$refs.editor, options)
        this.editor.pasteHTML(content)
        this.editor.on('text-change', () => this.delayUpdateEditor())
        if (this.canInsertImages) { this.configureInsertingImages() }
        this.$emit('setEditor', this.editor)
      },

      initQuillOptions() {
        let AlignStyle = Quill.import('attributors/style/align'),
          BackgroundStyle = Quill.import('attributors/style/background'),
          ColorStyle = Quill.import('attributors/style/color'),
          DirectionStyle = Quill.import('attributors/style/direction'),
          FontStyle = Quill.import('attributors/style/font'),
          SizeStyle = Quill.import('attributors/style/size')
        Quill.register(AlignStyle, true)
        Quill.register(BackgroundStyle, true)
        Quill.register(ColorStyle, true)
        Quill.register(DirectionStyle, true)
        Quill.register(FontStyle, true)
        Quill.register(SizeStyle, true)
      },

      insertImage(imageSrc) {
        this.editor.insertEmbed(this.editorIndex.index, 'resizeableImage', imageSrc)
        this.showImages = false
      },

      listenForImageClicks(event) {
        const $target = event.target
        if ($target.tagName === 'IMG') {
          this.setResizeImage($target)
        } else {
          this.resetResize()
        }
      },

      resetResize() {
        const resizeableElement = this.editor.root.querySelector('.resizable')
        if (this.imageToResize || resizeableElement) {
          this.dragIcon = null
          this.imageToResize = null
          resizeableElement && resizeableElement.classList.remove('resizable')
        }
      },

      setResizeDimension(dimensionType, dimension) {
        this.imageToResize.style[dimensionType] = dimension
      },

      setResizeImage($image) {
        const hasResizeImage = this.imageToResize
        if (!hasResizeImage || hasResizeImage.src !== $image.src) {
          this.resetResize()
          this.imageToResize = $image
          let icon = this.editor.root.querySelector(`img[src="${this.imageToResize.src}"]`),
            siblingIcon = icon.nextSibling,
            siblingIsDomElement = siblingIcon instanceof Element || siblingIcon instanceof HTMLDocument
          icon = siblingIsDomElement ? siblingIcon : icon
          if (icon instanceof Element || icon instanceof HTMLDocument) { this.dragIcon = icon }
        }
      },

      toggleOpenImages() {
        this.editorIndex = this.editor.getSelection()
        this.showImages = !this.showImages
      },
    },

    mounted() {
      this.initQuill()
    },
  }
</script>